<template>
  <div class="create-campaign-cover-upload">
    <input
      type="file"
      name="upload-cover-image"
      id="upload-cover-image"
      @change="onChange"
      ref="file"
      accept=".jpg, .jpeg, .png, .webp"
    >

    <label
      for="upload-cover-image"
      class="create-campaign-cover-upload__content"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <common-loader v-if="loading" style="max-height: 40px" />

      <template v-else-if="value">
        <img :src="value" alt="" class="create-campaign-cover-upload__preview">

        <div class="create-campaign-cover-upload__remove">
          <icons-close-circle-filled :width="21" :height="21" @click.native.prevent="removeFile" />
        </div>
      </template>

      <template v-else>
        <icons-upload :width="28" :height="28" color="currentColor" :stroke="0.5" />

        Cover image
        <span>browse files</span>
      </template>
    </label>
  </div>
</template>

<script>
import Api from '@/axios/api'

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_SIZE_BYTES = 10 * 1024 * 1024

export default {
  name: 'CreateCampaignCoverImageUpload',
  props: {
    value: {
      type: String,
      required: false,
      default: null,
    },
  },
  data: () => ({
    loading: false,
  }),
  methods: {
    onChange () {
      const file = this.$refs.file.files?.[0]
      if (file) { this.handleFile(file) }
    },
    handleFile (file) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        this.$refs.file.value = null
        this.$store.dispatch('snackbar/showSnackbar', { message: 'Please upload a JPG, PNG or WEBP image.', type: 'error' })
        return
      }

      if (file.size > MAX_SIZE_BYTES) {
        this.$refs.file.value = null
        this.$store.dispatch('snackbar/showSnackbar', { message: 'The cover image must not be larger than 10 MB.', type: 'error' })
        return
      }

      this.uploadFile(file)
    },
    removeFile () {
      this.$refs.file.value = null
      this.$emit('input', null)
    },
    dragover (event) {
      event.preventDefault()
      event.currentTarget.classList.add('create-campaign-cover-upload__content--file-hover')
    },
    dragleave (event) {
      event.currentTarget.classList.remove('create-campaign-cover-upload__content--file-hover')
    },
    drop (event) {
      event.preventDefault()
      const currentTarget = event.currentTarget

      if (event.dataTransfer?.files?.length) {
        this.$refs.file.files = event.dataTransfer.files
        this.onChange()
      }

      currentTarget.classList.remove('create-campaign-cover-upload__content--file-hover')
    },
    uploadFile (file) {
      const formData = new FormData()
      formData.append('file', file, file.name)
      this.loading = true

      Api.post('/customer/campaign-covers', formData)
        .then(({ success, data }) => {
          if (success) { this.$emit('input', data.url) }
        })
        .catch(({ response }) => {
          const errorMessage = response?.data?.errors?.file?.join(' ')
            ?? 'The cover image failed to upload.'
          this.$store.dispatch('snackbar/showSnackbar', { message: errorMessage, type: 'error' })
        })
        .finally(() => (this.loading = false))
    },
  },
}
</script>

<style lang="scss" scoped>
.create-campaign-cover-upload {
  & > input {
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
    position: absolute;
  }

  &__content {
    position: relative;
    min-width: 224px;
    max-width: 224px;
    min-height: 90px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    border: 1px dashed #95979D;
    background-color: #FAFAFA;
    transition: background-color ease-in-out 0.16s, border ease-in-out 0.08s;

    font-family: 'Lato-Italic', sans-serif;
    font-weight: 400;
    font-size: 15px;
    line-height: 18px;
    color: #95979D;

    & > span {
      color: #6E50FF;
      font-family: 'Lato-Regular', sans-serif;
      text-decoration: underline;
    }

    &:hover,
    &--file-hover {
      background-color: #EDFCFB;
      border: 1px solid #A7DAD8;
      color: #62646A;
    }
  }

  &__preview {
    object-fit: contain;
    max-width: 100%;
    max-height: 90px;
  }

  &__remove {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    color: #95979D;
    cursor: pointer;

    &:hover {
      color: #62646A;
    }
  }
}
</style>
